import React, { SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Popover, TextareaAutosize, Tooltip } from '@mui/material';
import styled from 'styled-components';

import LinkIconSrc from '@/assets/icons/link-2.svg';
import type { HyperlinkFormHandle } from '@/features/canvas/components/HyperlinkForm';
import { HyperlinkForm } from '@/features/canvas/components/HyperlinkForm';
import { ListStyledImage } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeCustom/InitiativeCustom';
import { CHARACTER_LIMITS } from '@/features/canvas/constants/character-limits';
import { CustomFieldType } from '@/features/canvas/constants/initiative-field';
import { useReservedProperty } from '@/features/canvas/hooks/useReservedProperty';
import { FieldType } from '@/features/canvas/types/field-type';
import { CharacterCounter } from '@/features/ui/components/CharacterCounter';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

import { InitiativeCustom } from '../InitiativeCustom';

const InitiativeBigCustom = ({
  readOnly,
  onDeleteField,
  onUpdateField,
  onClick,
  isLoading,
  isInCreationMode = false,
  field,
  isInitiativeLockedAndIsNotOwner,
}: {
  field: components['schemas']['InitiativeField'];
  isInitiativeLockedAndIsNotOwner: boolean;
  isLoading: boolean;
  onClick: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onUpdateField: (field: components['schemas']['InitiativeField']) => Promise<void>;
  readOnly: boolean;
  isInCreationMode?: boolean;
  onDeleteField?: (fieldId: number, e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const { reservedProperties } = useReservedProperty();

  const [inputValue, setInputValue] = useState<string>(field.value || '');
  const [hyperlinks, setHyperlinks] = useState(field.hyperlinks || []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hyperlinkFormRef = useRef<HyperlinkFormHandle | null>(null);

  const [selectionRange, setSelectionRange] = useState<{ end: number; start: number } | null>(null);

  const handleInputValueChange = (event: { target: { value: SetStateAction<string> } }) => {
    const newValue = event.target.value;
    if (typeof newValue === 'string' && newValue.length <= CHARACTER_LIMITS.LARGE_FIELD) {
      setInputValue(newValue);
    }
  };

  const handleOnSubmit = async (title: string, type: keyof typeof CustomFieldType) => {
    const trimmedTitle = title.trim();

    if (trimmedTitle.length > CHARACTER_LIMITS.TITLE) {
      showToast(
        'error',
        t('editor.sidebar.initiative.title_too_long', { max: CHARACTER_LIMITS.TITLE })
      );
      return;
    }

    if (
      isInCreationMode &&
      reservedProperties.some(property => property.title === trimmedTitle && property.type === type)
    ) {
      showToast('error', t('editor.sidebar.initiative.reserved_title_used'));
      return;
    }

    const updatedField = {
      ...field,
      value: inputValue,
      title: trimmedTitle,
      type: CustomFieldType.big,
      hyperlinks,
    };

    await onUpdateField(updatedField);
  };

  const handleHyperlinkClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hyperlinks.length > 0) {
      hyperlinkFormRef.current?.cancelNextCleanup();
    }

    if (selectionRange) {
      const selectedText = inputValue.slice(selectionRange.start, selectionRange.end);

      setHyperlinks(prev => {
        if (prev.length === 0) return prev;

        const updated = [...prev];

        updated[0] = {
          ...updated[0],
          display_text: selectedText,
          position_start: selectionRange.start,
          position_end: selectionRange.end,
        };

        return updated;
      });
      hyperlinkFormRef.current?.markDirty();
    }

    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = async () => {
    await hyperlinkFormRef.current?.flushPending?.();
    setAnchorEl(null);
  };

  const popoverOpen = Boolean(anchorEl);
  const popoverId = popoverOpen ? 'hyperlink-popover' : undefined;

  const hyperlinkButton = (
    <Tooltip title={t('Hyperlink')} placement="top">
      <StyledIconButton onClick={handleHyperlinkClick} size="small">
        <ListStyledImage src={LinkIconSrc} />
      </StyledIconButton>
    </Tooltip>
  );

  const handleTextSelection = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    if (start === end) {
      setSelectionRange(null);
    } else {
      setSelectionRange({ start, end });
    }
  };

  return (
    <>
      <InitiativeCustom
        field={field}
        handleSubmit={handleOnSubmit}
        isLoading={isLoading}
        onDeleteField={onDeleteField}
        readOnly={readOnly}
        isInCreationMode={isInCreationMode}
        isInitiativeLockedAndIsNotOwner={isInitiativeLockedAndIsNotOwner}
        extraActions={!readOnly && !isInitiativeLockedAndIsNotOwner ? hyperlinkButton : null}
      >
        <FieldWrapper>
          <StyledInputArea
            readOnly={readOnly || isInitiativeLockedAndIsNotOwner}
            value={inputValue || ''}
            minRows={1}
            maxRows={6}
            placeholder={t('editor.sidebar.initiative.max_char_count', {
              count: CHARACTER_LIMITS.LARGE_FIELD,
            })}
            onChange={handleInputValueChange}
            maxLength={CHARACTER_LIMITS.LARGE_FIELD}
            onClick={onClick}
            onMouseUp={handleTextSelection}
          />
          <CharacterCounter
            currentLength={inputValue.length}
            maxLength={CHARACTER_LIMITS.LARGE_FIELD}
          />
        </FieldWrapper>
      </InitiativeCustom>

      <Popover
        id={popoverId}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: theme => ({
            mt: '-77px',
            p: 2,
            maxWidth: 235,
            position: 'relative',
            overflow: 'visible',
            padding: 0,
            borderRadius: 2,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: 32,
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `16px solid ${theme.palette.background.paper}`,
              zIndex: 1,
            },
          }),
        }}
      >
        <PopoverContent>
          <HyperlinkForm
            ref={hyperlinkFormRef}
            hyperlinks={hyperlinks}
            setHyperlinks={setHyperlinks}
            fieldId={field.id!}
            fieldType={FieldType.InitiativeField}
            initiativeFieldType={field.type}
            selectionRange={selectionRange}
            inputValue={inputValue}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export { InitiativeBigCustom };

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledInputArea = styled(TextareaAutosize)`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  border-radius: 0.4rem;
  resize: vertical;
  max-height: 120px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.brand.backgroundDialog};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.brand.textSecondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.palette.brand.textPrimary};
    }
  }

  &:focus-visible {
    border: 1px solid ${({ theme }) => theme.palette.brand.textSecondary};
    outline: none;
  }
`;

const PopoverContent = styled.div`
  padding: 1rem;
  max-width: 400px;
`;

const StyledIconButton = styled(IconButton)`
  padding: 5px;
  width: auto;
  height: 25px;
`;
